import React, { useRef } from 'react';
import { Inputs } from '../../../../../types/Enums';
import {
  Icon,
  IconButton,
  Box,
  Button,
  HStack,
  Text,
  useMediaQuery,
  Stack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useFlowsSelector } from '../../../../../../../common/src/utils/globalVariables';
import { isEmpty } from 'lodash';
import useCurrentGroup from '../../../../../hooks/useCurrentGroup';
import useIsGroupSkippable from '../../../../../hooks/useIsGroupSkippable';
import SingleCheckboxInputComponent from '../../../../input/advisory-flow-inputs/single-checkbox/SingleCheckboxInputComponent';
import { bph } from '../../../../../../../common/src/utils/styledComponentsUtils';
import cogoToast from 'cogo-toast';
import { getInputConfig } from '../../../../input/inputConfig';

import { useLocation } from 'react-router';
import useMobileBreakpoints from '../../../../../../../common/src/hooks/useMobileBreakpoints';
import useCurrentGroupPosition from '../../../../../hooks/useCurrentGroupPosition';

interface Props {
  onBack: () => void;
  onContinue?: () => void;
  disabledContinue?: boolean;
  hideContinueButton?: boolean;
  isLoading?: boolean;
  isVertical?: boolean;
  noSkip?: boolean;
}

const QuestionGroupActions: React.FC<React.PropsWithChildren<Props>> = ({
  disabledContinue,
  hideContinueButton,
  onBack,
  onContinue = () => null,
  isLoading,
  children,
  isVertical,
  noSkip,
  ...otherProps
}) => {
  const footerRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const { t } = useTranslation();

  const { isMobileL, isLaptop } = useMobileBreakpoints();
  const isSmallScreen = !isMobileL;
  const formContext = useFormContext();
  const isGroupSkippable = useIsGroupSkippable();
  const flowId = useFlowsSelector((state) => state.flows.metaData.flowId);
  const inputUrl = useFlowsSelector((state) => state.flows.metaData.inputUrl);

  const { validationErrorMessage, toastErrorMessage, canSubmit } =
    useFlowsSelector((state) => state.flows.formState);

  const currentGroup = useCurrentGroup();
  const position = useCurrentGroupPosition();

  const [verticalPxSufficient] = useMediaQuery(`(min-height: ${bph.svga}px)`, {
    ssr: false,
  });

  // Check if a question has the config to hide the continue button
  const isContinueButtonHidden =
    currentGroup?.questions.some(
      (question) =>
        getInputConfig(flowId)[question?.rules.input_type]?.gridConfig
          .isContinueButtonHidden,
    ) && location.pathname.includes('/flows/');

  // TODO: Convert isContinueButtonHidden to use this once new selection view is done
  // const { isContinueButtonHidden } = useGetInputGridProps();

  const scrollFade = {
    content: '""',
    position: 'absolute',
    zIndex: '1',
    bottom: '99%',
    left: '0',
    pointerEvents: 'none',
    w: '100%',
    h: '10',
    backgroundImage:
      'linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(253, 252, 255, 1) 100%)',
  };

  const isYesNoQuestion =
    currentGroup?.questions[0]?.rules.input_type === Inputs.SINGLE_CHECKBOX;

  const isGoingBackPossible = position > 0 || inputUrl;

  const handleOnContinue = () => {
    if (toastErrorMessage) {
      cogoToast.error(toastErrorMessage, { hideAfter: 5 });
    } else {
      onContinue();
    }
  };

  return (
    <Box
      ref={footerRef}
      flexShrink={0}
      w="100%"
      position={verticalPxSufficient ? 'sticky' : 'static'}
      bottom="0"
      right="0"
      zIndex="1"
      p={{ base: '4', laptop: '6' }}
      pb={{ laptop: '8' }}
      borderTop={{ base: '1px', laptop: 'none' }}
      borderColor="gray.50"
      bg={{ base: 'white', laptop: '#fdfcff' }}
      _before={{ laptop: scrollFade }}
      {...otherProps}
    >
      {(validationErrorMessage || !isEmpty(formContext?.formState.errors)) && (
        <Text
          textStyle="normalBody"
          color="danger.main"
          textAlign="center"
          mb="4"
        >
          {validationErrorMessage ||
            (isSmallScreen
              ? t('FLOWS_QUESTIONS_VALIDATION-ERROR_MESSAGE-SHORT')
              : t('FLOWS_QUESTIONS_VALIDATION-ERROR_MESSAGE'))}
        </Text>
      )}
      <Stack
        direction={isVertical ? 'column-reverse' : 'row'}
        spacing={{ base: '3', tablet: '6' }}
        justifyContent="center"
        {...(isVertical && { alignItems: 'center' })}
      >
        {isGoingBackPossible && (
          <>
            {!isLaptop && (
              <IconButton
                onClick={onBack}
                variant="secondary"
                borderRadius="full"
                aria-label="Back"
                icon={<Icon name="GeneralActionsArrowLeft" />}
                minH="12"
                minW="12"
              />
            )}
            <Button
              display={{ base: 'none', laptop: 'inherit' }}
              leftIcon={<Icon name="GeneralActionsArrowLeft" />}
              minH="12"
              minW="64"
              onClick={onBack}
              borderRadius="full"
              variant="grayedGhost"
            >
              {t('Go back')}
            </Button>
          </>
        )}
        {!hideContinueButton && !isContinueButtonHidden && (
          <Button
            data-test-id="ProdFac_LargeButton"
            minH="12"
            minW={{ base: '48', laptop: '64' }}
            onClick={handleOnContinue}
            borderRadius="full"
            w={{ base: '100%', laptop: 'auto' }}
            isDisabled={
              formContext?.formState.isSubmitting ||
              isLoading ||
              disabledContinue ||
              !canSubmit
            }
            isLoading={formContext?.formState.isSubmitting || isLoading}
          >
            {isGroupSkippable && !noSkip ? t('SKIP') : t('Continue')}
          </Button>
        )}
        {children && (
          <HStack flexGrow={{ base: 1, laptop: 0 }}>{children}</HStack>
        )}
        {isYesNoQuestion && formContext && !isLaptop && (
          <HStack flexGrow={1}>
            <SingleCheckboxInputComponent
              question={currentGroup?.questions[0]}
            />
          </HStack>
        )}
      </Stack>
    </Box>
  );
};

export default QuestionGroupActions;
